"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { type StoreApi, useStore } from "zustand";
import { clearPendingCheckout, readPendingCheckout } from "./pending-checkout";
import { createPermitStore, resumeCheckout } from "./permitStore";
import type { EntrySelection, PermitFormStore, WizardSettings } from "./types";

export const PermitStoreContext = createContext<StoreApi<PermitFormStore> | undefined>(undefined);

type PermitStoreProviderProps = {
	children: React.ReactNode;
	initialValues?: {
		wizardSettings: WizardSettings;
		formData?: Pick<PermitFormStore["formData"], "vehicles" | "email">;
		isParkingTimeStepForced?: boolean;
		entrySelection?: EntrySelection;
	};
};

export const PermitStoreProvider = ({ children, initialValues }: PermitStoreProviderProps) => {
	const [{ store, isResumed }] = useState(() => {
		const pending = readPendingCheckout();
		const resumed =
			pending && initialValues
				? resumeCheckout({
						wizardSettings: initialValues.wizardSettings,
						formData: pending.formData,
						isParkingTimeStepForced: initialValues.isParkingTimeStepForced,
					})
				: undefined;
		return {
			store: createPermitStore({
				formData: initialValues?.formData,
				wizardSettings: initialValues?.wizardSettings,
				isParkingTimeStepForced: initialValues?.isParkingTimeStepForced,
				entrySelection: initialValues?.entrySelection,
				resumed,
			}),
			isResumed: !!resumed,
		};
	});

	useEffect(() => {
		if (isResumed) {
			clearPendingCheckout();
		}
	}, [isResumed]);

	return <PermitStoreContext.Provider value={store}>{children}</PermitStoreContext.Provider>;
};

const usePermitFormStore = <T,>(selector: (state: PermitFormStore) => T) => {
	const store = useContext(PermitStoreContext);
	if (!store) {
		throw new Error("usePermitFormStore must be used within PermitStoreProvider");
	}

	return useStore(store, selector);
};

export const usePermitFormData = () => usePermitFormStore((state) => state.formData);

export const usePermitSteps = () => usePermitFormStore((state) => state.steps);

export const usePermitActiveStep = () => usePermitFormStore((state) => state.activeStep);

export const usePermitError = () => usePermitFormStore((state) => state.error);

export const useIsProcessingPermit = () => usePermitFormStore((state) => state.isProcessingPermit);

export const usePermitFormActions = () => usePermitFormStore((state) => state.actions);

export const usePermitWizardSettings = () => usePermitFormStore((state) => state.wizardSettings);

export const usePermitEntrySelection = () => usePermitFormStore((state) => state.entrySelection);

export const useResumedEntrySelection = () => useState(() => readPendingCheckout()?.entrySelection)[0];
